import { createContext, useContext } from 'react';
import { defineMessages } from 'react-intl';

import { glossary } from '#src/lib/glossary.ts';
import { messages as walletMessages } from '#src/lib/wallet.ts';

export const notifications = {
  copiedToClipboard: glossary.copiedToClipboard,
  unconfirmedDevice: walletMessages.unconfirmedDevice,
  ...defineMessages({
    bridgeApproval: {
      defaultMessage:
        "We're authorizing deposits to the bridge contract. Please wait for this transaction to complete before doing a deposit",
    },
    cancelOffer: {
      defaultMessage: 'Sale has been canceled',
    },
    confirmWithdrawal: {
      defaultMessage:
        'Please confirm your withdrawal through the confirmation email that has been sent to you.',
    },
    ethereumCard: {
      defaultMessage:
        'Your Card has been deposited. It might take a few minutes to appear in your gallery.',
    },
    ethereumAccountLinked: {
      defaultMessage: 'Your Ethereum account {address} has been linked',
    },
    composeLineup: {
      defaultMessage: 'Your team has been saved!',
    },
    consentProxyManagementFailed: {
      defaultMessage: 'Consent proxy management failed, please try again.',
    },
    errors: {
      defaultMessage: 'Errors: {errors}',
    },
    recoveryEmailVerificationUnavailable: {
      defaultMessage:
        'Your recovery email was added, but we could not open the verification step. Reload the page to verify it.',
    },
    walletKeysUnavailable: {
      defaultMessage:
        'We could not retrieve your wallet keys. Please check your connection and try again.',
    },
    threeDsError: {
      defaultMessage: '3D Secure Failed: {message}.',
    },
    baseballDraftMoreThanFiveOnTheSameTeam: {
      defaultMessage:
        'You have {nbDrafted} players from the {teamName}. You have to swap out one player to meet the criteria of maximum {max} players per team.',
    },
    baseballDraftAutofillErrorExceedBudget: {
      defaultMessage:
        'You do not have enough budget remaining to auto complete a team',
    },
    baseballTeamRegistered: {
      defaultMessage: 'Team registered successfully',
    },
    lostInternetConnection: {
      defaultMessage: 'You appear offline, most actions might not work.',
    },
    tooManySubscriptions: {
      defaultMessage:
        'Live updates are paused because Sorare is open in too many tabs or windows. Close a few to turn real-time updates back on.',
    },
    serviceUnderMaintenance: {
      defaultMessage:
        'Maintenance operation in progress from {start} to {end}: {msg}',
    },
    passwordForgottenError: {
      defaultMessage: 'An error has occured. Please try again later.',
    },
    passwordForgottenSuccess: {
      defaultMessage: 'Please check your email to reset your password.',
    },
    confirmDeviceRequestSent: {
      defaultMessage:
        'Please check your {delivery, select, email {email} phone {phone} other {email or phone}} to confirm the current device.',
    },
    promoApplied: {
      defaultMessage:
        'Promotion has been successfully applied and will be available after your signup',
    },
    deviceSuccessfullyConfirmed: {
      defaultMessage: 'Current device successfully confirmed.',
    },
    signUp: {
      defaultMessage:
        'Welcome {nickname}! Please check your emails to confirm your account',
    },
    directOfferCancelled: {
      defaultMessage: 'Offer cancelled',
    },
    directOfferAccepted: {
      defaultMessage: 'Offer accepted',
    },
    directOfferRejected: {
      defaultMessage: 'Offer rejected',
    },
    so5FixtureDelete: {
      defaultMessage: 'Your team has been deleted!',
    },
    so5LineupsConfirm: {
      defaultMessage: 'Your teams have been confirmed',
    },
    offerSent: {
      defaultMessage: 'Your offer has been sent',
    },
    '2faEnabled': {
      defaultMessage:
        'Two-factor authentication has been successfully enabled on your account',
    },
    '2faDisabled': {
      defaultMessage:
        'Two-factor authentication has been successfully disabled on your account',
    },
    unlockWallet: {
      defaultMessage: 'Please unlock your wallet to be able to proceed',
    },
    txSent: {
      defaultMessage: 'Transaction has been sent successfully',
    },
    phoneNumberVerified: {
      defaultMessage: 'Your phone number has been successfully verified!',
    },
    paymentMethodDetached: {
      defaultMessage: 'Your credit card has been removed',
    },
    secondFactorRecommendation: {
      defaultMessage:
        'To ensure the security of your assets on Sorare, we strongly recommend that you <link>enable a second authentication factor</link>.',
    },
    confirmEmail: {
      defaultMessage:
        '<strong>You still need to confirm your email.</strong> Check your email to confirm your account {remainingHours, plural, =0 {within the coming minutes} one {within the hour} other {within the next # hours}} to avoid getting locked out.<link>Resend confirmation email</link>',
    },
    unknownReferrer: {
      defaultMessage:
        "The username <strong>{referrer}</strong> doesn't exist. Please try another referral link.",
    },
    nullSorarePrivateKeyRecovery: {
      defaultMessage:
        'Your wallet is not pending recovery. If you recently changed your password you will receive a new email within 24 hours.',
    },
    mangopayWalletVerificationSumitted: {
      defaultMessage:
        'Thank you for submitting your document. You will be notified once the review is processed.',
    },
    termsAccepted: {
      defaultMessage: 'You have accepted the terms of service, please sign in',
    },
    logOutAllDevices: {
      defaultMessage:
        'You’ve been successfully logged out from all other devices besides this one',
    },
    accountingExtractSent: {
      defaultMessage:
        'You should receive an email with your accounting extract within the coming minutes.',
    },
    recoveryEmailRegistered: {
      defaultMessage:
        '{email} has been successfully added to your recovery methods.',
    },
    walletSuccessfullyRecovered: {
      defaultMessage: 'You’ve recovered your wallet.',
    },
    cashWalletNamesUpdatedDefault: {
      defaultMessage: 'Success! Your name has been updated.',
    },
    cashWalletNamesUpdatedAskingKycAgain: {
      defaultMessage:
        'Success! Your name has been updated. Please upload your ID to resume access to cash deposits, withdrawals, and rewards.',
    },
    uploadError: {
      defaultMessage: 'Oops! Something went wrong during the file upload.',
    },
    oauthMissingEmail: {
      defaultMessage: 'You cannot create a Sorare account without an email.',
    },
    oauthLockedAccount: {
      defaultMessage: 'Your account is suspended, please use another email.',
    },
    reminderSuccessfullyAddedOnAuction: {
      defaultMessage:
        'Notifications are on for this auction! Follow it in <myAuctionsLink>My Auctions</myAuctionsLink>.',
    },
    reminderSuccessfullyRemovedFromAuction: {
      defaultMessage: 'Notifications are off for this auction.',
    },
    competitionSubscribed: {
      defaultMessage: '{displayName} added to Favorite Competitions',
    },
    competitionUnsubscribed: {
      defaultMessage: '{displayName} removed from Favorite Competitions',
    },
    importLinuepsDeclined: {
      defaultMessage:
        'You have already declined this import for your lineups from {provider}',
    },
    importLinuepsExpired: {
      defaultMessage: 'This import of your lineups from {provider} has expired',
    },
    commonCardDropped: {
      defaultMessage: 'Card dropped successfully',
    },
    paymentFailed: {
      defaultMessage: 'Payment failed, please try again later.',
    },
    watchlistCreated: {
      defaultMessage: '“{title}” created successfully <link>View</link>',
    },
    watchlistUpdated: {
      defaultMessage: '“{title}” updated successfully',
    },
    watchlistDestroyed: {
      defaultMessage: '”{title}” deleted successfully',
    },
    reportUserSuccess: {
      defaultMessage: 'Your report has been submitted. Thank you.',
    },
    playerAddedToWatchlist: {
      defaultMessage: '{player} added to {watchlist} <link>View</link>',
    },
    scaFailed: {
      defaultMessage: 'SCA failed, please try again.',
    },
    scaStatusNotChanged: {
      defaultMessage:
        'Your SCA request has not been validated yet, please try to refresh the page or try again later.',
    },
    cartLimitReached: {
      defaultMessage: 'You cannot add more than {max} items to your cart.',
    },
    squadNotFound: {
      defaultMessage:
        'Squad not found. The invitation link may be invalid or expired.',
    },
    recoveryKeySent: {
      defaultMessage:
        'Recovery key sent to {destination}. If you don’t get it, check your spam folder.',
    },
    recoveryKeyAlreadySent: {
      defaultMessage:
        'A recovery key was already sent to {destination}. Check your inbox — you can request a new one in a few minutes.',
    },
  } as const),
};

export enum Level {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

type Ids = keyof typeof notifications;
export type SnackNotificationOptions = {
  level?: Level;
  link?: string;
  onClosed?: () => void;
  autoHideDuration?: null | number;
};

export type Notification = {
  id: Ids;
  values?: any;
  options?: {
    level?: Level;
    link?: string;
    onClosed?: () => void;
    ignoreDuplicates?: boolean;
  };
};

export interface SnackNotificationContext {
  showNotification: (
    id: Ids,
    values?: any,
    opts?: SnackNotificationOptions
  ) => void;
  clearNotification: () => void;
  notification: Notification | null;
}

export const SnackNotificationContext =
  createContext<SnackNotificationContext | null>(null);

export const useSnackNotificationContext = () =>
  useContext(SnackNotificationContext)!;
